import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import type { Kpi } from "../lib/types";
import { fmt, fmt1 } from "../lib/format";
import { getKpi } from "../lib/api";

const MONTH_FULL_RU = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];

function monthLabel(monthId: string): string {
  const [y, m] = monthId.split("-").map(Number);
  return `${MONTH_FULL_RU[(m ?? 1) - 1]} ${y}`;
}

interface MonthRow {
  monthId: string;
  bonusUah: number;
  debt: number;
  totalKpi: number;
  bonusPct: number;
}

export function BonusScreen({ kpi }: { kpi: Kpi }) {
  const { managerId, period, periodId, t, availableMonths } = useAuth();

  function periodToMonthsLocal(): string[] {
    if (period === "month") return [periodId];
    if (period === "quarter") {
      const m = periodId.match(/^Q(\d)-(\d{4})$/);
      if (!m) return [];
      const q = +m[1]!, y = +m[2]!;
      const start = (q - 1) * 3 + 1;
      return [0,1,2].map(o => `${y}-${String(start+o).padStart(2,"0")}`).filter(id => availableMonths.includes(id));
    }
    if (period === "half") {
      const m = periodId.match(/^H(\d)-(\d{4})$/);
      if (!m) return [];
      const h = +m[1]!, y = +m[2]!;
      const start = (h - 1) * 6 + 1;
      return Array.from({length:6}, (_,o) => `${y}-${String(start+o).padStart(2,"0")}`).filter(id => availableMonths.includes(id));
    }
    if (period === "year") {
      const m = periodId.match(/^Y-(\d{4})$/);
      if (!m) return [];
      const y = +m[1]!;
      return Array.from({length:12}, (_,o) => `${y}-${String(o+1).padStart(2,"0")}`).filter(id => availableMonths.includes(id));
    }
    return [];
  }
  const [byMonth, setByMonth] = useState<MonthRow[]>([]);

  useEffect(() => {
    if (period === "month") { setByMonth([]); return; }
    const months = periodToMonthsLocal();
    let cancelled = false;
    Promise.all(months.map(m => getKpi(managerId, "month", m).then(r => r.kpi.hasData ? {
      monthId: m,
      bonusUah: r.kpi.bonusUah,
      debt: r.kpi.debt,
      totalKpi: r.kpi.totalKpiAvg,
      bonusPct: r.kpi.bonusPctAvg,
    } : null).catch(() => null))).then(rows => {
      if (cancelled) return;
      setByMonth(rows.filter((x): x is MonthRow => x !== null));
    });
    return () => { cancelled = true; };
  }, [managerId, period, periodId]);

  const isMulti = kpi.monthsCount > 1;
  const hasDebt = kpi.debt > 0;

  return (
    <section className="p-4 space-y-3">
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-5 shadow-lg">
        <div className="text-xs opacity-80 uppercase tracking-wide">{t("bonus_earned")}</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-4xl font-bold">{fmt(kpi.bonusUah)}</span>
          <span className="text-lg opacity-90">грн</span>
        </div>
        <div className="text-xs opacity-80 mt-1">
          {isMulti ? t("bonus_for_period", { count: kpi.monthsCount }) : (periodId && period === "month" ? monthLabel(periodId) : "")}
        </div>
        {hasDebt && (
          <div className="mt-3 pt-3 border-t border-white/25 text-xs">
            <div className="flex justify-between opacity-85"><span>{t("bonus_debt_label")}</span><span>−{fmt(kpi.debt)} грн</span></div>
            <div className="flex justify-between mt-1"><span className="opacity-85">{t("bonus_payout_label")}</span><span>{fmt(kpi.payout)} грн</span></div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{t("bonus_how")}</div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">{t("bonus_total_kpi")}</span>
          <span className="font-semibold text-slate-900">{fmt1(kpi.totalKpiAvg)}%{isMulti ? t("bonus_avg_suffix") : ""}</span>
        </div>
        {kpi.premKpiSum > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">{t("bonus_prem_kpi")}</span>
            <span className="text-slate-700">+{fmt1(isMulti ? kpi.premKpiAvg : kpi.premKpiSum)}%{isMulti ? " (в среднем)" : ""}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">{t("bonus_pct")}</span>
          <span className="font-semibold text-slate-900">{fmt1(kpi.bonusPctAvg)}%{isMulti ? t("bonus_avg_suffix") : ""}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">{t("bonus_dept_base")}</span>
          <span className="font-semibold text-slate-900">{fmt(kpi.dept.fact)} грн</span>
        </div>
        <div className="border-t border-slate-200 pt-2 flex justify-between text-base">
          <span className="text-slate-900 font-semibold">{t("bonus_earned_label")}</span>
          <span className="font-bold text-emerald-700">{fmt(kpi.bonusUah)} грн</span>
        </div>
        {hasDebt && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t("bonus_debt_table")}</span>
              <span className="text-slate-700">−{fmt(kpi.debt)} грн</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">{t("bonus_payout_table")}</span>
              <span className="text-slate-700">{fmt(kpi.payout)} грн</span>
            </div>
          </>
        )}
      </div>

      {isMulti && byMonth.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">{t("bonus_by_month")}</div>
          <div className="space-y-2">
            {byMonth.map(r => {
              const debtTxt = r.debt > 0 ? ` ${t("bonus_by_month_debt", { value: fmt(r.debt) })}` : "";
              return (
                <div key={r.monthId} className="flex justify-between items-baseline text-sm border-b border-slate-100 pb-1.5 last:border-0">
                  <div>
                    <div className="text-slate-800 font-medium">{monthLabel(r.monthId)}</div>
                    <div className="text-[10px] text-slate-500">KPI {fmt1(r.totalKpi)}% · бонус {fmt1(r.bonusPct)}%{debtTxt && <span className="text-rose-500">{debtTxt}</span>}</div>
                  </div>
                  <div className="font-semibold text-emerald-700">{fmt(r.bonusUah)} грн</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-[10px] text-slate-500 px-1 leading-relaxed">{t("bonus_formula")}</div>
    </section>
  );
}
